// Ein-/Ausblenden der Widgets (README §5.2, §10).
// localStorage['immo-whide'] = { o: { <key>: true }, u: { <key>: true } }
// o = Objekt-Widgets, u = Einheit-Widgets. Gilt für alle Objekte bzw. Einheiten.

import { useCallback, useState } from 'react';

export type WidgetScope = 'o' | 'u';
export type Ausgeblendet = Record<WidgetScope, Record<string, true>>;

export const WHIDE_SCHLUESSEL = 'immo-whide';

/** Standard: KI-Aliase und Ehemalige sind beim Objekt ausgeblendet. */
export function standardAusgeblendet(): Ausgeblendet {
  return { o: { Aliase: true, Ehem: true }, u: {} };
}

function istScopeObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === 'object' && wert !== null && !Array.isArray(wert);
}

function bereinigeScope(wert: unknown): Record<string, true> {
  const aus: Record<string, true> = {};
  if (!istScopeObjekt(wert)) return aus;
  for (const [k, v] of Object.entries(wert)) if (v === true) aus[k] = true;
  return aus;
}

/** Liest den gespeicherten Wert robust; Unbrauchbares ergibt den Standard. */
export function parseAusgeblendet(roh: string | null): Ausgeblendet {
  if (!roh) return standardAusgeblendet();
  try {
    const wert: unknown = JSON.parse(roh);
    if (!istScopeObjekt(wert)) return standardAusgeblendet();
    return { o: bereinigeScope(wert.o), u: bereinigeScope(wert.u) };
  } catch {
    return standardAusgeblendet();
  }
}

export function istAusgeblendet(h: Ausgeblendet, scope: WidgetScope, key: string): boolean {
  return h[scope][key] === true;
}

export function schalteWidget(h: Ausgeblendet, scope: WidgetScope, key: string): Ausgeblendet {
  const neu = { ...h[scope] };
  if (neu[key]) delete neu[key];
  else neu[key] = true;
  return { ...h, [scope]: neu };
}

/** „Alle einblenden“ für einen Scope. */
export function alleEinblenden(h: Ausgeblendet, scope: WidgetScope): Ausgeblendet {
  return { ...h, [scope]: {} };
}

/** „Standard“ für einen Scope. */
export function scopeZuruecksetzen(h: Ausgeblendet, scope: WidgetScope): Ausgeblendet {
  return { ...h, [scope]: { ...standardAusgeblendet()[scope] } };
}

/** Anzahl sichtbarer Widgets aus einer Liste angebotener Keys. */
export function anzahlSichtbar(h: Ausgeblendet, scope: WidgetScope, keys: readonly string[]): number {
  return keys.filter(k => !istAusgeblendet(h, scope, k)).length;
}

function lese(): Ausgeblendet {
  try {
    return parseAusgeblendet(localStorage.getItem(WHIDE_SCHLUESSEL));
  } catch {
    return standardAusgeblendet();
  }
}

/** Hook: Zustand + Setter, der sofort speichert. */
export function useWidgetSichtbarkeit(): [Ausgeblendet, (h: Ausgeblendet) => void] {
  const [h, setH] = useState<Ausgeblendet>(lese);
  const speichere = useCallback((neu: Ausgeblendet) => {
    try {
      localStorage.setItem(WHIDE_SCHLUESSEL, JSON.stringify(neu));
    } catch {
      // ohne Speicher gilt die Einstellung nur bis zum Neuladen
    }
    setH(neu);
  }, []);
  return [h, speichere];
}
